import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GraduationCap, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(3, 'Password is required'),
});

export default function StudentLogin() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/students/login`, data);
      localStorage.setItem('studentToken', response.data.access_token);
      toast.success('Login successful!');
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <GraduationCap className="h-10 w-10 text-brand-blue" />
            <span className="text-3xl font-bold text-brand-dark font-display">Tutor Flex</span>
          </div>
          <h1 className="text-2xl font-bold text-brand-dark">Student Login</h1>
          <p className="text-slate-600 mt-2">Access your learning dashboard</p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200">
          <form onSubmit={handleSubmit(onSubmit)} data-testid="student-login-form" className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-brand-dark font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="student-email-input"
                {...register('email')}
                placeholder="your.email@example.com"
                className="mt-2 rounded-lg"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-brand-dark font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="student-password-input"
                {...register('password')}
                placeholder="Enter your password"
                className="mt-2 rounded-lg"
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="student-login-btn"
              disabled={isSubmitting}
              className="w-full bg-brand-dark hover:bg-slate-800 text-white rounded-lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <p className="text-slate-600 text-sm">
              Don't have an account?{' '}
              <Link to="/student/register" className="text-brand-blue hover:underline font-medium">
                Register here
              </Link>
            </p>
            <p>
              <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-brand-blue">
                Forgot your password?
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-600 hover:text-brand-blue text-sm">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
